import { useEffect, useState } from 'react'
import type { MortalityLog } from '../../types/production'
import { useT } from '../../i18n'
import { CrewCorrectionButton } from '../crewCorrection/CrewCorrectionButton'
import { CrewRowBadges, useCorrectionCounts } from '../crewCorrection/CrewRowBadges'
import { useActorNames } from '../../utils/actorNames'

/**
 * MORT-12 drill-down popover.
 *
 * Listed in [docs/release/v2.1/findings.md](../../../docs/release/v2.1/findings.md)
 * row #5: the `(from N events)` badge on Database and Biomass cards promised
 * evidence that was not previously accessible. This component renders the list
 * of underlying `MortalityLog` events when the badge is clicked.
 *
 * Per-event Delete (finding #9) is opt-in via `onDeleteEvent`. It is the only
 * UI path that can remove orphan events whose owning ProductionRecord row was
 * already deleted before the Datatable → linked-log propagation landed
 * (finding #8). Sampling-row deletes continue to remove the linked event via
 * `findLinkedMortalityLog` + `deleteLogRow` in [src/screens/Biomass.tsx](../Biomass.tsx).
 *
 * v2.2 findings #66: the delete lives inside the audited "Corregir…" dialog
 * (reason required, prior row kept); `onDeleteEvent` performs it.
 */
export interface MortalityEventsPopoverProps {
  events: MortalityLog[]
  lotLabel: string
  dateLabel: string
  totalCount: string
  onClose: () => void
  onDeleteEvent?: (eventId: string, reason: string) => Promise<void> | void
  /** Farm for the (feed-catalog is unused here, but kept for parity) correction surface. */
  farmId?: string
  /** Crew Mode supervisor correction v0 — called after a per-event correction is saved. */
  onCorrectedEvent?: () => Promise<void> | void
}

const formatEventTime = (recordedAt: string): string => {
  if (!recordedAt) return ''
  const parsed = new Date(recordedAt)
  if (Number.isNaN(parsed.getTime())) return recordedAt
  try {
    return parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return parsed.toISOString().slice(11, 16)
  }
}

const formatHeaderDate = (rawDate: string): string => {
  if (!rawDate) return ''
  const parsed = new Date(rawDate)
  if (Number.isNaN(parsed.getTime())) return rawDate
  try {
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return parsed.toISOString().slice(0, 10)
  }
}

const sortEventsByTime = (events: MortalityLog[]): MortalityLog[] => {
  return [...events].sort((a, b) => {
    const aTime = new Date(a.recordedAt).getTime()
    const bTime = new Date(b.recordedAt).getTime()
    const aValid = !Number.isNaN(aTime)
    const bValid = !Number.isNaN(bTime)
    if (aValid && bValid) return aTime - bTime
    if (aValid) return -1
    if (bValid) return 1
    return 0
  })
}

export const mortalityEventsPopoverHelpers = {
  formatEventTime,
  formatHeaderDate,
  sortEventsByTime
}

export const MortalityEventsPopover = ({
  events,
  lotLabel,
  dateLabel,
  totalCount,
  onClose,
  onDeleteEvent,
  farmId,
  onCorrectedEvent
}: MortalityEventsPopoverProps) => {
  const t = useT()
  const formattedDate = formatHeaderDate(dateLabel)
  const showActions = Boolean(onDeleteEvent || onCorrectedEvent)
  // findings #55: Crew / Corregido badges and who logged each event.
  const actorNames = useActorNames()
  const [correctionsRefreshKey, setCorrectionsRefreshKey] = useState(0)
  const correctionCounts = useCorrectionCounts(
    'mortality-log',
    events.map((event) => event.id),
    correctionsRefreshKey
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [onClose])

  const sorted = sortEventsByTime(events)

  return (
    <div
      data-testid="mortality-events-popover"
      role="dialog"
      aria-modal="true"
      aria-label={t('datatable.mortality.events.popover.title', { lot: lotLabel, date: formattedDate })}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: 12,
          minWidth: 320,
          maxWidth: 560,
          width: '100%',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)'
        }}
      >
        <header
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>
              {t('datatable.mortality.events.popover.title', { lot: lotLabel, date: formattedDate })}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#475569' }}>
              {t('datatable.mortality.events.popover.total', {
                count: String(events.length),
                sum: totalCount
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('datatable.mortality.events.popover.close')}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 20,
              color: '#475569',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </header>
        <div style={{ overflowY: 'auto', padding: '12px 20px 20px' }}>
          {sorted.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
              {t('datatable.mortality.events.popover.empty')}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#475569' }}>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
                    {t('datatable.mortality.events.popover.column.time')}
                  </th>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, textAlign: 'right' }}>
                    {t('datatable.mortality.events.popover.column.count')}
                  </th>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
                    {t('datatable.mortality.events.popover.column.notes')}
                  </th>
                  {showActions ? (
                    <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, textAlign: 'right' }}>
                      {t('datatable.mortality.events.popover.column.actions')}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {sorted.map((event) => (
                  <tr key={event.id}>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', whiteSpace: 'nowrap' }}>
                      {formatEventTime(event.recordedAt)}
                      <CrewRowBadges
                        captureSource={event.captureSource}
                        capturedByActorId={event.capturedByActorId}
                        correctionCount={correctionCounts.get(event.id) ?? 0}
                        actorNames={actorNames}
                        metadata={event.metadata}
                      />
                    </td>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', textAlign: 'right', fontWeight: 600 }}>
                      {event.mortalityCount || '0'}
                    </td>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>
                      {(event.notes ?? '').trim() || '—'}
                    </td>
                    {showActions ? (
                      <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <CrewCorrectionButton
                            resource="mortality-log"
                            entityId={event.id}
                            farmId={farmId}
                            onDelete={onDeleteEvent ? async (reason) => { await onDeleteEvent(event.id, reason) } : undefined}
                            onDone={async () => {
                              setCorrectionsRefreshKey((key) => key + 1)
                              await onCorrectedEvent?.()
                            }}
                          />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default MortalityEventsPopover
